/*!
 * Free Gift with Every Order (supports multiple independent gift rules)
 * -----------------------------------------------------------------------
 * Vanilla JS, no dependencies. Reads its settings from the JSON blocks
 * printed by snippets/free-gift.liquid (one <script data-free-gift-config>
 * per gift rule), so nothing here is hard-coded to any specific product.
 *
 * Each gift rule runs completely independently (its own popup, its own
 * "already tried this cart" memory, its own eligibility check), so a
 * second/third gift rule never interferes with the first one.
 *
 * How it stays in sync with the theme (T4S):
 *  - T4S dispatches `document` CustomEvent "cart:updated" after every one
 *    of its own AJAX cart actions (product page ATC, Quick add, Quick
 *    view, cart drawer qty +/-, cart page qty +/-, remove links, etc).
 *  - T4S listens for `document` CustomEvent "cart:refresh" and, in
 *    response, re-fetches & re-renders the cart drawer + cart page +
 *    cart count bubble via Shopify's Section Rendering API.
 *  - So instead of hooking every possible "add to cart" entry point
 *    ourselves (fragile), we just listen for "cart:updated", reconcile
 *    each gift line item against the real Shopify cart (source of truth,
 *    via GET /cart.js), and when we need to add/remove/fix a gift we do
 *    a plain fetch to Shopify's own AJAX Cart API and then dispatch
 *    "cart:refresh" ourselves so the theme repaints everything the same
 *    way it always does. No core theme file needs to be touched.
 */
(function () {
  "use strict";

  var configEls = document.querySelectorAll("[data-free-gift-config]");
  if (!configEls.length) return; // feature disabled / nothing configured

  var sharedCartPromise = null;

  function fetchCart() {
    // Share one in-flight /cart.js request across all gift instances that
    // reconcile around the same time, instead of each firing its own.
    if (sharedCartPromise) return sharedCartPromise;
    sharedCartPromise = fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .finally(function () {
        sharedCartPromise = null;
      });
    return sharedCartPromise;
  }

  function isAnyGiftLine(item) {
    // True for a gift line belonging to ANY gift rule (shared property
    // flag), used to exclude every gift line when counting "real" items.
    return !!(item && item.properties && item.properties["_Free_Gift"] === "true");
  }

  function initFreeGift(configEl, index) {
    var CFG;
    try {
      CFG = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    if (!CFG || !CFG.enabled || !CFG.variantId) return;

    var ROUTES = {
      add: CFG.cartAddUrl || "/cart/add.js",
      change: CFG.cartChangeUrl || "/cart/change.js"
    };

    var popupId = CFG.popupId || "t4s-free-gift-popup";
    var SIG_KEY = "t4s_free_gift_tried_sig_" + (CFG.instanceId || index);

    var isBusy = false; // per-instance mutex so overlapping reconciles don't race
    var pendingRecheck = false;

    function isOwnGiftLine(item) {
      return (
        item &&
        Number(item.variant_id) === Number(CFG.variantId) &&
        item.properties &&
        item.properties[CFG.propertyKey] === CFG.propertyValue
      );
    }

    function addGift() {
      var body = { id: CFG.variantId, quantity: 1, properties: {} };
      body.properties[CFG.propertyKey] = CFG.propertyValue;

      return fetch(ROUTES.add, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json();
      });
    }

    function setGiftQuantity(lineKey, quantity) {
      return fetch(ROUTES.change, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      }).then(function (r) {
        return r.json();
      });
    }

    function refreshThemeUI(openDrawer) {
      document.dispatchEvent(new CustomEvent(openDrawer ? "cart:refresh:opend" : "cart:refresh"));
    }

    function cartSignature(items) {
      var parts = [];
      for (var i = 0; i < items.length; i++) {
        if (!isAnyGiftLine(items[i])) parts.push(items[i].variant_id + "x" + items[i].quantity);
      }
      return parts.sort().join("|");
    }

    function getTriedSig() {
      try {
        return sessionStorage.getItem(SIG_KEY);
      } catch (e) {
        return null;
      }
    }
    function setTriedSig(v) {
      try {
        if (v) sessionStorage.setItem(SIG_KEY, v);
        else sessionStorage.removeItem(SIG_KEY);
      } catch (e) {}
    }

    function isFreeLine(item) {
      return Number(item.final_line_price) === 0;
    }

    function isCartEligible(realItems, realItemCount) {
      var minQty = Number(CFG.minQty) || 1;
      if (realItemCount < minQty) return false;

      // "any" (or no trigger list configured) = eligible for every order,
      // same behavior as the original single-gift feature.
      if (!CFG.triggerType || CFG.triggerType === "any") return true;

      var triggerIds = Array.isArray(CFG.triggerProductIds)
        ? CFG.triggerProductIds.map(function (id) {
            return Number(id);
          })
        : [];
      if (!triggerIds.length) return true; // nothing picked = applies store-wide, like gift #1

      for (var i = 0; i < realItems.length; i++) {
        if (triggerIds.indexOf(Number(realItems[i].product_id)) !== -1) return true;
      }
      return false;
    }

    function reconcile() {
      if (isBusy) {
        pendingRecheck = true;
        return;
      }
      isBusy = true;

      fetchCart()
        .then(function (cart) {
          var items = cart.items || [];
          var giftLine = null;
          var realItems = [];
          var realItemCount = 0;

          for (var i = 0; i < items.length; i++) {
            if (isOwnGiftLine(items[i])) {
              giftLine = items[i];
            } else if (!isAnyGiftLine(items[i])) {
              realItems.push(items[i]);
              realItemCount += items[i].quantity;
            }
          }

          var sig = cartSignature(items);
          var eligible = isCartEligible(realItems, realItemCount);

          // 1) Cart isn't eligible for this gift rule: it must not be there.
          if (!eligible) {
            setTriedSig(null);
            if (giftLine) {
              return setGiftQuantity(giftLine.key, 0).then(function () {
                refreshThemeUI(false);
              });
            }
            return null;
          }

          // 2) Gift is in cart.
          if (giftLine) {
            if (!isFreeLine(giftLine)) {
              // Shopify didn't discount it (conditions not met, discount
              // expired, price not 0, ...) -> remove it.
              setTriedSig(sig);
              return setGiftQuantity(giftLine.key, 0).then(function () {
                refreshThemeUI(false);
              });
            }
            setTriedSig(null);
            if (giftLine.quantity !== 1) {
              return setGiftQuantity(giftLine.key, 1).then(function () {
                refreshThemeUI(false);
              });
            }
            return null;
          }

          // 3) No gift yet. Don't retry for a cart we already know doesn't qualify.
          if (getTriedSig() === sig) return null;

          return addGift().then(function (res) {
            if (!res || res.status) return null; // add failed (out of stock, ...)
            return fetch("/cart.js", { headers: { Accept: "application/json" } })
              .then(function (r) {
                return r.json();
              })
              .then(function (cart2) {
                var g = null;
                var its = cart2.items || [];
                for (var j = 0; j < its.length; j++) {
                  if (isOwnGiftLine(its[j])) {
                    g = its[j];
                    break;
                  }
                }
                if (g && !isFreeLine(g)) {
                  setTriedSig(sig);
                  return setGiftQuantity(g.key, 0).then(function () {
                    refreshThemeUI(false);
                  });
                }
                setTriedSig(null);
                refreshThemeUI(!!CFG.openCartOnAdd);
                if (CFG.showPopup) showPopup();
                return null;
              });
          });
        })
        .catch(function (err) {
          if (window.Shopify && window.Shopify.designMode) {
            console.warn("[Free Gift] reconcile failed (" + popupId + "):", err);
          }
        })
        .finally(function () {
          isBusy = false;
          if (pendingRecheck) {
            pendingRecheck = false;
            reconcile();
          }
        });
    }

    /* ---------------------------------------------------------------- */
    var popupTimer = null;

    function showPopup() {
      var popup = document.getElementById(popupId);
      if (!popup) return;

      popup.hidden = false;
      popup.setAttribute("aria-hidden", "false");
      void popup.offsetWidth;
      popup.classList.add("is--open");

      if (CFG.enableAnimation) {
        popup.classList.add("is--animated");
      } else {
        popup.classList.remove("is--animated");
      }

      clearTimeout(popupTimer);
      var duration = Number(CFG.popupDuration) || 5000;
      popupTimer = setTimeout(hidePopup, duration);
    }

    function hidePopup() {
      var popup = document.getElementById(popupId);
      if (!popup) return;
      clearTimeout(popupTimer);
      popup.classList.remove("is--open");
      popup.setAttribute("aria-hidden", "true");
      setTimeout(function () {
        popup.hidden = true;
      }, 300);
    }

    document.addEventListener("click", function (e) {
      var closeBtn = e.target.closest && e.target.closest("[data-free-gift-close]");
      if (closeBtn && closeBtn.closest("#" + popupId)) {
        hidePopup();
      }
    });

    document.addEventListener("keyup", function (e) {
      if (e.keyCode !== 27) return;
      var popup = document.getElementById(popupId);
      if (popup && popup.classList.contains("is--open")) hidePopup();
    });

    document.addEventListener("cart:updated", reconcile);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", reconcile);
    } else {
      reconcile();
    }
  }

  configEls.forEach(function (el, index) {
    initFreeGift(el, index + 1);
  });
})();
